import { useMemo, useState } from 'react';
import { useQuery } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { ProvisioningTable } from './ProvisioningTable';

type AuditLogRow = {
  createdAt: string;
  source: string;
  action: string;
  actorEmail?: string;
  actorUserId?: string;
  summary: string;
  errorDetail?: string;
  result: 'ok' | 'error';
};

interface AuditTabProps {
  customerId: string;
}

export function AuditTab({ customerId }: AuditTabProps) {
  const [sourceFilter, setSourceFilter] = useState<string>('');

  const logs = useQuery(backendApi.audit.list, {
    customerId,
    limit: 100,
    source: sourceFilter === 'ui' || sourceFilter === 'chat' ? sourceFilter : undefined,
  });

  const rows = useMemo(() => logs ?? [], [logs]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Audit log</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Provisioning and chat actions for this customer
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={sourceFilter === '' ? 'primary' : 'secondary'}
            onClick={() => setSourceFilter('')}
          >
            All
          </Button>
          <Button
            size="sm"
            variant={sourceFilter === 'ui' ? 'primary' : 'secondary'}
            onClick={() => setSourceFilter('ui')}
          >
            UI
          </Button>
          <Button
            size="sm"
            variant={sourceFilter === 'chat' ? 'primary' : 'secondary'}
            onClick={() => setSourceFilter('chat')}
          >
            Chat
          </Button>
        </div>
      </div>

      <Card animate={false} className="p-0 overflow-hidden">
        {logs === undefined ? (
          <Loader text="Loading audit log..." />
        ) : (
          <ProvisioningTable
            columns={[
              { key: 'time', label: 'Time' },
              { key: 'source', label: 'Source' },
              { key: 'action', label: 'Action' },
              { key: 'actor', label: 'Actor' },
              { key: 'summary', label: 'Summary' },
              { key: 'result', label: 'Result' },
            ]}
            rows={rows.map((log: AuditLogRow) => ({
              time: new Date(log.createdAt).toLocaleString(),
              source: log.source,
              action: <span className="font-mono text-xs">{log.action}</span>,
              actor: log.actorEmail || log.actorUserId || '—',
              summary: (
                <span>
                  {log.summary}
                  {log.errorDetail && (
                    <span className="block text-xs text-red-600 dark:text-red-400 mt-0.5">
                      {log.errorDetail}
                    </span>
                  )}
                </span>
              ),
              result: (
                <Badge variant={log.result === 'ok' ? 'success' : 'danger'}>{log.result}</Badge>
              ),
            }))}
            emptyMessage="No audit entries yet."
          />
        )}
      </Card>
    </div>
  );
}